import { cn } from "@base/lib/utils";
import { Pagination } from "@base/ui/Pagination";
import { adminQueries } from "@features/admin/api/queryFactory";
import type { MemberListItem, MemberStatus, MemberType } from "@features/admin/model/types";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MemberDetailPanel } from "./MemberDetailPanel";
import { MemberFilters } from "./MemberFilters";
import { MembersTable } from "./MembersTable";

export const MembersTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: "/myinfo" });
  const { page, company_name, type, status } = useSearch({ from: "/_authenticated/myinfo" });
  // page는 선택 파라미터이므로(일반 유저는 URL에 page가 없다) 여기서만 기본값 1을 적용한다.
  const currentPage = page ?? 1;
  const [selectedMember, setSelectedMember] = useState<MemberListItem | null>(null);

  const { data, isLoading, isFetching, isError } = useQuery(
    adminQueries.members({ page: currentPage, company_name, type, status }),
  );

  const members = data?.results ?? [];
  const count = data?.count ?? 0;
  const pageSize = data?.page_size ?? 10;
  const totalPages = data?.total_pages ?? 1;
  // Showing 범위는 응답의 current_page 기준으로 계산한다(keepPreviousData 중에는 URL page와 어긋날 수 있다).
  const rangePage = data?.current_page ?? currentPage;
  const from = count === 0 ? 0 : (rangePage - 1) * pageSize + 1;
  const to = Math.min(rangePage * pageSize, count);

  // 검색어나 필터가 바뀌면 현재 페이지가 존재하지 않을 수 있으므로 1쪽으로 되돌린다.
  const updateSearch = (patch: Partial<{ company_name?: string; type?: MemberType; status?: MemberStatus }>) => {
    navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-title4 text-gray-1000">{t("adminMembersTitle")}</h2>

      <MemberFilters
        search={company_name ?? ""}
        type={type}
        status={status}
        onSearchChange={(value) => updateSearch({ company_name: value || undefined })}
        onTypeChange={(value) => updateSearch({ type: value })}
        onStatusChange={(value) => updateSearch({ status: value })}
      />

      {isLoading ? (
        <div className="py-12 text-center text-gray-500 text-label3">{t("adminLoading")}</div>
      ) : isError ? (
        // 조회 실패를 "멤버 0명"으로 오인하지 않도록 빈 상태와 분리해서 표시한다.
        <div className="py-12 text-center text-red-500 text-label3">{t("adminLoadFailed")}</div>
      ) : members.length === 0 ? (
        <div className="py-12 text-center text-gray-500 text-label3">{t("adminEmptyMembers")}</div>
      ) : (
        <div className="relative">
          {/* 검색/필터 갱신 중에는 이전 결과를 그대로 둔 채 표 위쪽에 로딩바만 띄운다. */}
          {isFetching && (
            <div
              className="overflow-hidden absolute inset-x-0 top-0 z-10 h-1 rounded-t-8 bg-primary-10"
              aria-hidden="true"
            >
              <div className="w-1/3 h-full bg-primary-500 animate-indeterminate" />
            </div>
          )}
          <div className={cn("transition-opacity duration-200", isFetching && "opacity-50 pointer-events-none")}>
            <MembersTable members={members} onRowClick={setSelectedMember} />

            <div className="flex justify-between items-center mt-4">
              <span className="text-gray-700 text-label2">{t("adminShowingRange", { total: count, from, to })}</span>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(next) => navigate({ search: (prev) => ({ ...prev, page: next }) })}
              />
            </div>
          </div>
        </div>
      )}

      <MemberDetailPanel member={selectedMember} onClose={() => setSelectedMember(null)} />
    </div>
  );
};
